import { Injectable } from '@angular/core';
import { BaseProvider } from '../base';
import { DatasetProvider } from '../dataset';
import { BrainVersionType } from '../brain';
import { AssetListingProvider } from '../asset_listing';
import { ClassProvider, Class } from '../class';
import { RecognitionFrame } from '../frame';
import { AnomalyStatsService } from './anomaly-stats.service';
import { FrameProvider } from '../frame';

export enum EvaluatePageTab {
    WorkingBrain = 'WorkingBrain',
    StaticBrain = 'StaticBrain', // Used for Static Classifier brains
    HiFiBrain = 'HiFiBrain' // Used for Anomaly Hi-Fi brains
}

export enum ExpandedPageMode {
    NONE = 'none',
    VALIDATION_SET = 'validationSet',
    VALIDATION_RESULTS = 'validationResults',
    VALIDATION_IMAGE_DETAILS = 'validationImageDetails',
    WORKSPACE = 'workspace'
}

export interface HiFiPreviewOverlayMap {
    [key: string]: string;
}

@Injectable()
export class EvaluateBrainProvider extends BaseProvider {
    private recognitionFrames: RecognitionFrame[];
    private filteredRecognitionFrames: RecognitionFrame[];

    private classesTable: Map<string, Class>;

    // Values passed in from the validation set component.
    private filteringClassID: string;
    private filteringResults: string;

    // Used to persist information about where we are in the validation set for the workspace page
    private evaluateImageDetailsPageNumber: number;

    constructor(
        private datasetProvider: DatasetProvider,
        private assetListingProvider: AssetListingProvider,
        private classProvider: ClassProvider,
        private anomalyStatsService: AnomalyStatsService,
        private frameProvider: FrameProvider
    ) {
        super();
    }

    /**
     * Individual variable getters/setters
     */
    public getRecognitionFrames(): RecognitionFrame[] {
        return this.recognitionFrames;
    }

    public pageHasRecognitionFrames(): boolean {
        return this.recognitionFrames && this.recognitionFrames.length > 0;
    }

    public getFilteredRecognitionFrames(): RecognitionFrame[] {
        return this.filteredRecognitionFrames;
    }

    public getClassesTable(): Map<string, Class> {
        return this.classesTable;
    }

    public getEvaluateImageDetailsPageNumber(): number {
        return this.evaluateImageDetailsPageNumber;
    }

    public setEvaluateImageDetailsPageNumber(page: number) {
        this.evaluateImageDetailsPageNumber = page;
    }

    public cleanUpFilterState(): void {
        this.filteringClassID = '';
        this.filteringResults = '';
    }

    public async fetchRecognitionFrames(
        datasetIdentifier: string,
        brainVersionType: BrainVersionType,
        isTestSet: boolean = false
    ): Promise<void> {
        try {
            if (isTestSet) {
                this.recognitionFrames = await this.frameProvider.getTestRecognitionFrames(datasetIdentifier, 0, 9999);
            } else {
                this.recognitionFrames = await this.frameProvider.getValidationRecognitionFrames(
                    datasetIdentifier,
                    0,
                    9999,
                    brainVersionType
                );
            }

            this.classesTable = new Map();

            const datasetClassNameMap = this.classProvider.getDatasetClassNameMap(datasetIdentifier);
            const nothingIKnowClass = this.classProvider.getNothingIKnowClass(datasetIdentifier);

            this.recognitionFrames.forEach((asset: RecognitionFrame) => {
                if (!this.classesTable.has(asset.actualClass) && datasetClassNameMap[asset.actualClass]) {
                    this.classesTable.set(asset.actualClass, datasetClassNameMap[asset.actualClass]);
                }

                if (!this.classesTable.has(asset.predictedClass) && datasetClassNameMap[asset.predictedClass]) {
                    this.classesTable.set(asset.predictedClass, datasetClassNameMap[asset.predictedClass]);
                }

                if (
                    isTestSet &&
                    !asset.actualClass &&
                    this.classProvider.isClassNothingIKnow(asset.predictedClass, false)
                ) {
                    this.classesTable.set(nothingIKnowClass.name, nothingIKnowClass);
                }
            });

            this.filteredRecognitionFrames = this.assetListingProvider.applyRecognitionFramesFilter(
                this.recognitionFrames,
                this.filteringClassID,
                this.filteringResults,
                this.classesTable
            );
        } catch (error) {
            throw error;
        }
    }

    public onNormalThresholdChanged(newValue: number, datasetIdentifier: string): void {
        const dataset = this.datasetProvider.getDatasetById(datasetIdentifier);
        if (!dataset) {
            return;
        }

        const tempRecognitionFrames = this.recognitionFrames.map(v => {
            const predictedClass = v.normalScore == null || v.normalScore >= newValue ? 'Normal' : 'Anomaly';

            const testOutcome = this.anomalyStatsService.convertResultNameToInitials(
                this.anomalyStatsService.getSingleAnomalyTestOutcome(
                    v.actualClass,
                    predictedClass,
                    dataset.truePositivePreference
                )
            );

            return {
                anomalyOverlaySignedUrl: v.anomalyOverlaySignedUrl,
                assetIdentifier: v.assetIdentifier,
                normalScore: v.normalScore,
                name: v.name,
                prefix: v.prefix,
                actualClass: v.actualClass,
                predictedClass,
                signedThumbnailUrl: v.signedThumbnailUrl,
                testOutcome
            };
        });

        this.recognitionFrames = tempRecognitionFrames;
    }

    public updateFilteringClassForRecognitionFrames(newClassId: string): void {
        this.filteringClassID = newClassId;
        this.filteredRecognitionFrames = this.assetListingProvider.applyRecognitionFramesFilter(
            this.recognitionFrames,
            this.filteringClassID,
            this.filteringResults,
            this.classesTable
        );
    }

    public updateFilteringResultsForRecognitionFrames(newResults: string): void {
        this.filteringResults = newResults;
        this.filteredRecognitionFrames = this.assetListingProvider.applyRecognitionFramesFilter(
            this.recognitionFrames,
            this.filteringClassID,
            this.filteringResults,
            this.classesTable
        );
    }
}



// WEBPACK FOOTER //
// ./src/providers/evaluate_brain/evaluate_brain.ts